/**
 * 路由系统
 * 处理 SPA 路由和页面切换
 */

export class Router {
  constructor() {
    this.routes = new Map();
    this.currentRoute = null;
    this.params = {};
    this.beforeHooks = [];
    this.afterHooks = [];
  }
  
  /**
   * 注册路由
   * @param {string} path - 路径，支持 :param 参数
   * @param {Function} handler - 处理函数
   */
  on(path, handler) {
    // 转换路径为正则
    const pattern = path
      .replace(/\//g, '\\/')
      .replace(/:([^\/]+)/g, '(?<$1>[^\\/]+)');
    
    this.routes.set(new RegExp(`^${pattern}$`), handler);
    return this;
  }
  
  /**
   * 导航到指定路径
   * @param {string} path
   */
  navigate(path) {
    history.pushState(null, '', path);
    this.handle();
  }
  
  /**
   * 替换当前历史记录
   * @param {string} path
   */
  replace(path) {
    history.replaceState(null, '', path);
    this.handle();
  }
  
  /**
   * 返回上一页
   */
  back() {
    history.back();
  }
  
  /**
   * 添加前置钩子
   * @param {Function} hook
   */
  beforeEach(hook) {
    this.beforeHooks.push(hook);
  }
  
  /**
   * 添加后置钩子
   * @param {Function} hook
   */
  afterEach(hook) {
    this.afterHooks.push(hook);
  }
  
  /**
   * 处理当前路由
   */
  async handle() {
    const path = window.location.pathname;
    
    // 执行前置钩子
    for (const hook of this.beforeHooks) {
      const result = await hook(path);
      if (result === false) return;
    }
    
    // 匹配路由
    let matched = false;
    for (const [pattern, handler] of this.routes) {
      const match = path.match(pattern);
      if (match) {
        this.params = match.groups || {};
        this.currentRoute = path;
        
        try {
          await handler(this.params);
        } catch (err) {
          console.error('Route handler error:', err);
          this.handleError(err);
        }
        
        matched = true;
        break;
      }
    }
    
    if (!matched) {
      this.handleNotFound(path);
    }
    
    // 执行后置钩子
    for (const hook of this.afterHooks) {
      await hook(path);
    }
  }
  
  /**
   * 处理 404
   * @param {string} path
   */
  handleNotFound(path) {
    console.warn('Route not found:', path);
    // 可以重定向到首页或显示 404 页面
    if (path !== '/') {
      this.navigate('/');
    }
  }
  
  /**
   * 处理错误
   * @param {Error} err
   */
  handleError(err) {
    console.error('Router error:', err);
  }
  
  /**
   * 初始化路由
   */
  init() {
    // 监听浏览器前进后退
    window.addEventListener('popstate', () => this.handle());
    
    // 拦截链接点击
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="/"]');
      if (link) {
        const href = link.getAttribute('href');
        // 检查是否是内部链接
        if (href.startsWith('/') && !href.startsWith('//')) {
          e.preventDefault();
          this.navigate(href);
        }
      }
    });
    
    // 初始路由
    this.handle();
  }
  
  /**
   * 获取 URL 参数
   * @param {string} key
   * @returns {string|undefined}
   */
  getParam(key) {
    return this.params[key];
  }
  
  /**
   * 获取查询参数
   * @param {string} key
   * @returns {string|null}
   */
  getQuery(key) {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
  }
}

// 单例导出
export const router = new Router();
